/**
 * When a panel is dragged from the customize toolbox into a section, the dash sortable layer
 * (see {@link DashSortableService}) invokes this **after** `panels-order.json` has been persisted
 * so each {@link HomeDashPanelComponent} can load UI for the dropped panel (by {@link HomeDashPanelModel.panelId}).
 *
 * `invoke` may run in the same synchronous turn as sortable `stop`, before the destination
 * `HomeDashPanelComponent` has registered — handlers are deferred and retried, and {@link register}
 * drains any pending drop for that `panelId`.
 */

export class DashPanelToolboxBridge {
    private static readonly handlersByPanelId = new Map<number, () => void>();
    /** `invoke` ran before a handler existed; {@link register} runs the handler once. */
    private static readonly pendingPanelIds = new Set<number>();

    /**
     * @returns Deregister; call from `effect` cleanup or `DestroyRef.onDestroy`.
     */
    static register(panelId: number, handler: () => void): () => void {
        DashPanelToolboxBridge.handlersByPanelId.set(panelId, handler);
        queueMicrotask(() => DashPanelToolboxBridge.flushPendingIfReady(panelId));
        return () => {
            if (DashPanelToolboxBridge.handlersByPanelId.get(panelId) === handler) {
                DashPanelToolboxBridge.handlersByPanelId.delete(panelId);
            }
            DashPanelToolboxBridge.pendingPanelIds.delete(panelId);
        };
    }

    static invoke(panelId: number): void {
        queueMicrotask(() => {
            if (DashPanelToolboxBridge.tryRunHandler(panelId)) {
                return;
            }
            DashPanelToolboxBridge.pendingPanelIds.add(panelId);
            const attemptDeferred = (): void => {
                if (!DashPanelToolboxBridge.pendingPanelIds.has(panelId)) {
                    return;
                }
                void DashPanelToolboxBridge.tryRunHandler(panelId);
            };
            window.setTimeout(attemptDeferred, 0);
            window.setTimeout(attemptDeferred, 50);
        });
    }

    private static flushPendingIfReady(panelId: number): void {
        if (!DashPanelToolboxBridge.pendingPanelIds.has(panelId)) {
            return;
        }
        void DashPanelToolboxBridge.tryRunHandler(panelId);
    }

    /** @returns true if a handler ran (or was cleared as run). */
    private static tryRunHandler(panelId: number): boolean {
        const handler = DashPanelToolboxBridge.handlersByPanelId.get(panelId);
        if (!handler) {
            return false;
        }
        DashPanelToolboxBridge.pendingPanelIds.delete(panelId);
        handler();
        return true;
    }
}
